import { DefaultIcon, FileContentType, FileContentTypeDispute, FileContentTypeMapping, SDate } from '@summit/core';
import { SummitFormattedText } from '@summit/models';
import { Dayjs } from 'dayjs';
import { EboEventTo } from '../../base-classes/ebo-event-to';
import { RerouteReason } from '../../digital-logs/reroute-reason.enum';
import { Company } from '../../organization/company/company';
import { Hub } from '../../places/hub/hub';
import { DisputeFile } from './dispute-file';
import { DisputeJournal } from './dispute-journal';
import { DisputeReconcile } from './dispute-reconcile';
import { DisputeStatus } from './dispute-status.enum';

export class Dispute extends EboEventTo {
  static defaultDescription = '<div>Fill in Description</div>';
  //add Settlement company
  companyId: string;
  company: Company;
  hubId: number;
  hub: Hub;
  beginDateTime: Dayjs;
  endDateTime: Dayjs;
  description: string;
  vendorReferenceNumber: string;
  status: DisputeStatus;
  disputeReconciles: DisputeReconcile[];
  journals: DisputeJournal[];
  files: DisputeFile[];
  amount: number;

  constructor(jsonData?: Partial<Dispute>) {
    super(jsonData);
    if (jsonData) {
      if (jsonData.hub) this.hub = new Hub(jsonData.hub);
      if (jsonData.company) this.company = new Company(jsonData.company);
      if (jsonData.beginDateTime) this.beginDateTime = SDate.newUtc(jsonData.beginDateTime);
      if (jsonData.endDateTime) this.endDateTime = SDate.newUtc(jsonData.endDateTime);
      if (jsonData.disputeReconciles)
        this.disputeReconciles = jsonData.disputeReconciles
          .map((reconcile) => new DisputeReconcile(reconcile))
          .sort((a, b) => {
            const dateDiff = a.reconcile.settlementDate.diff(b.reconcile.settlementDate);
            if (dateDiff === 0) return a.reconcile.settlementNum > b.reconcile.settlementNum ? 1 : -1;
            return dateDiff;
          });
      if (jsonData.journals) this.journals = jsonData.journals.map((update) => new DisputeJournal(update));
      if (jsonData.files) this.files = jsonData.files.map((file) => new DisputeFile(file));
    }
    this.disputeReconciles ??= [];
    this.journals ??= [];
    this.files ??= [];
    this.status ??= DisputeStatus.Open;
    this.eventDate ??= SDate.getNormalized();
    this.beginDateTime ??= SDate.getNormalized().add(-1, 'day').startOf('day');
    this.endDateTime ??= SDate.getNormalized().add(1, 'day').endOf('day');
  }

  //This is going to be HTML not formatted text
  private _updateSummary: string;
  get updateSummary(): string {
    if (this._updateSummary === undefined) {
      if (this.journals.length > 0) {
        this._updateSummary = '';
        this.journals.forEach((update) => {
          this._updateSummary += update.description + '<br/>';
        });
      }
      this._updateSummary ??= null;
    }
    return this._updateSummary;
  }

  private _reconcileSummary: SummitFormattedText;
  get reconcileSummary(): SummitFormattedText {
    if (this._reconcileSummary === undefined) {
      this._reconcileSummary = new SummitFormattedText();
      this.disputeReconciles.forEach((reconcile) => {
        const reconcileSummary = reconcile.reconcileSummary;
        if (!reconcileSummary.isEmpty()) {
          this._reconcileSummary.AddText(reconcileSummary);
        }
      });
      this._reconcileSummary ??= null;
    }
    return this._reconcileSummary;
  }

  private _fileSummary: SummitFormattedText;
  get fileSummary(): SummitFormattedText {
    if (this._fileSummary === undefined) {
      this._fileSummary = new SummitFormattedText();
      FileContentTypeDispute.filter((contentType) => contentType !== FileContentType.Attachment)
        .filter(
          (contentType) =>
            contentType !== FileContentType.WeatherMap ||
            this.disputeReconciles.filter(
              (dReconcile) =>
                dReconcile.reconcile.rerouted &&
                dReconcile.reconcile.asks.filter((trip) => trip.rerouteReason == RerouteReason.Weather)
            ).length > 0
        )
        .forEach((contentType: number) => {
          if (this.files.findIndex((ref) => ref.contentType === contentType) > -1)
            this._fileSummary.AddLine(
              FileContentTypeMapping[contentType],
              ['text-green-500'],
              null,
              DefaultIcon.CheckBox
            );
          else
            this._fileSummary.AddLine(
              FileContentTypeMapping[contentType],
              ['text-red-500'],
              null,
              DefaultIcon.UnCheckBox
            );
        });
      const attachmentCount = this.files.filter((ref) => ref.contentType === FileContentType.Attachment).length;
      if (attachmentCount > 0) {
        this._fileSummary.AddLine(
          `Other Attachments (${attachmentCount})`,
          ['text-green-500'],
          null,
          DefaultIcon.CheckBox
        );
      }

      this._fileSummary ??= null;
    }
    return this._fileSummary;
  }
}
